// Cross-batch registry of capture_items re-home targets.
//
// When a createItem PUT collides on its (session, space, asset) scope
// (already_exists), the carve-out re-homes the count onto the server's
// authoritative row at a DIFFERENT id and deletes the local orphan. The
// surveyor's +/- increments were queued as their own capture_items PATCH
// entries against the orphan id; this registry lets the PATCH dispatcher
// redirect them onto the survivor instead of PUTing a now-deleted id → 404 →
// dropped count (connector-dispatch-item-patch.ts).
//
// MODULE-LEVEL, not per-batch DispatchContext, so the mapping survives ACROSS
// CRUD batches within an app session. PowerSync's getCrudBatch caps a batch
// at 100 entries, so a large offline field session (a building easily exceeds
// 100 mutations) can upload the colliding create in one batch and its
// increments in a LATER batch — a per-batch map would be discarded between
// them and the increments would drop (Codex P1 on PR #630). uploadData runs
// batches sequentially in the same JS context, so module state bridges them.
// Mirrors the module-level `lwwAttempts` in connector-dispatch-item-patch.ts.
//
// Keys are client-generated UUIDs (globally unique), so a stale entry can
// never mis-match a different row; entries are tiny and bounded by the number
// of distinct collisions in a session.
//
// Residual: the mapping is in-memory, so a full app restart in the narrow
// window between the create's batch acking and its increments' later batch
// still falls back to the not_found drop. Durable (local SQLite) persistence
// would close that, but is disproportionate to the restart-mid-upload window
// and would add client-schema surface — deferred.

/**
 * FK / capture context of the AUTHORITATIVE row a re-homed increment lands on.
 *
 * Captured by the createItem carve-out at re-home time — it holds the full create
 * opData (the remapped session/asset ids it POSTed plus the row's space/building/
 * site/captured_at). A thin increment PATCH does NOT carry this, and the carve-out
 * DELETEs the local capture_items row, so without this context a re-homed
 * increment that must later be quarantined (its authoritative row was deleted
 * before it uploaded) lands in `orphaned_capture_items` with NULL FKs + NULL
 * captured_at — the recovery surface (rescue-held-captures.ts LEFT JOINs on
 * space_id / capture_session_id) then cannot place the count back into a
 * room/session (H17). Carrying it keeps the quarantined row placeable.
 *
 * All fields `string | null`: a legacy registry entry (recorded before this field
 * existed) or an absent column degrades to the prior NULL-context behaviour — a
 * still-recoverable count value, never a crash.
 */
export interface CaptureItemRehomeContext {
  readonly capture_session_id: string | null
  readonly site_id: string | null
  readonly building_id: string | null
  readonly space_id: string | null
  readonly asset_type_id: string | null
  readonly captured_at: string | null
}

/** Authoritative target a re-homed capture_item increment must PUT against. */
export interface CaptureItemRehomeTarget {
  /** Server row id that survived the (session, space, asset) collision. */
  readonly id: string
  /** Best-known current syncVersion of that row (post create-item carve-out). */
  readonly syncVersion: number
  /**
   * FK/capture context of the authoritative row, for placeable quarantine (H17).
   * Optional: a version-only refresh (putRehomedIncrement) omits it; see the
   * sticky-merge note on recordCaptureItemRehome.
   */
  readonly context?: CaptureItemRehomeContext
}

const captureItemRehomes = new Map<string, CaptureItemRehomeTarget>()

/**
 * Record orphan id → authoritative target (set by the createItem carve-out).
 *
 * `context` is STICKY. The carve-out records the full FK/capture context ONCE at
 * re-home time; later version-only refreshes (putRehomedIncrement, after each
 * increment lands) re-record the SAME orphan with a fresh syncVersion but no
 * context, and MUST NOT erase it — else a cross-batch SECOND increment that then
 * quarantines would write NULL FKs again (H17). The authoritative row's scope is
 * invariant across increments (the orphan id is DELETEd after the first re-home
 * and never re-created), so preserving the first-recorded context is always
 * correct.
 */
export function recordCaptureItemRehome(
  orphanId: string,
  target: CaptureItemRehomeTarget,
): void {
  const existing = captureItemRehomes.get(orphanId)
  captureItemRehomes.set(orphanId, {
    id: target.id,
    syncVersion: target.syncVersion,
    context: target.context ?? existing?.context,
  })
}

/** Look up the authoritative target for an orphan id, if it was re-homed. */
export function getCaptureItemRehome(
  orphanId: string,
): CaptureItemRehomeTarget | undefined {
  return captureItemRehomes.get(orphanId)
}

/** Test-only reset so cases don't leak rewrites across `it` blocks. */
export function _resetCaptureItemRehomes(): void {
  captureItemRehomes.clear()
}
